import { useMemo } from 'react';
import {
  type AssignmentDto,
  aggregateRange,
  formatMonthYear,
  formatTotalDays,
  localDateFromDay,
  monthSlotRange,
  shiftMonth,
  todayDayIndex,
  yearSlotRange,
} from '@betreuung/shared';
import { useAssignmentRange } from '@/api/queries';
import { useAppData } from '@/app/appData';
import { type StatsPeriod, useNavigation } from '@/app/navigation';
import { TabHeader } from '@/app/TabHeader';
import { useIsDesktop } from '@/app/useMediaQuery';
import { type SegmentedOption, SegmentedControl } from '@/components/shared/SegmentedControl';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';
import { Donut } from './Donut';
import { StackedBar } from './StackedBar';
import { StatAverageRow, StatRowItem, statRowKey } from './StatRowItem';

const MODE_OPTIONS: readonly SegmentedOption<StatsPeriod['mode']>[] = [
  { value: 'month', label: de.stats.modeMonth },
  { value: 'year', label: de.stats.modeYear },
];

/** How far the period picker reaches into past and future. */
const MONTH_WINDOW = 24;
const YEAR_WINDOW = 5;

/** Stable identity while the query is loading, so the aggregate is not redone. */
const NO_ASSIGNMENTS: AssignmentDto[] = [];

interface PeriodOption {
  value: string;
  label: string;
}

/** Months as a single sortable number, so option values need no parsing rules. */
function monthKey(year: number, month: number): number {
  return year * 12 + (month - 1);
}

function monthOptions(year: number, month: number): PeriodOption[] {
  const options: PeriodOption[] = [];
  for (let delta = -MONTH_WINDOW; delta <= MONTH_WINDOW; delta += 1) {
    const cursor = shiftMonth(year, month, delta);
    options.push({
      value: String(monthKey(cursor.year, cursor.month)),
      label: formatMonthYear(cursor.year, cursor.month),
    });
  }
  return options;
}

function yearOptions(year: number): PeriodOption[] {
  const options: PeriodOption[] = [];
  for (let delta = -YEAR_WINDOW; delta <= YEAR_WINDOW; delta += 1) {
    options.push({ value: String(year + delta), label: String(year + delta) });
  }
  return options;
}

/**
 * Statistics for one month or one year. Both modes aggregate the same assignment
 * window on the client; "Nicht zugewiesen" is a row like any other, which is why
 * an empty period simply reads as 100 %.
 */
export function StatsTab() {
  const { persons, now } = useAppData();
  const { statsPeriod, setStatsPeriod } = useNavigation();
  const isDesktop = useIsDesktop();

  const todayDay = todayDayIndex(now);
  const today = useMemo(() => localDateFromDay(todayDay), [todayDay]);

  const range =
    statsPeriod.mode === 'month'
      ? monthSlotRange(statsPeriod.year, statsPeriod.month)
      : yearSlotRange(statsPeriod.year);
  const assignments = useAssignmentRange(range);
  const summary = useMemo(
    () => aggregateRange(assignments.data ?? NO_ASSIGNMENTS, range.fromSlot, range.toSlot),
    [assignments.data, range.fromSlot, range.toSlot],
  );

  const periodValue =
    statsPeriod.mode === 'month'
      ? String(monthKey(statsPeriod.year, statsPeriod.month))
      : String(statsPeriod.year);
  const periodLabel =
    statsPeriod.mode === 'month'
      ? formatMonthYear(statsPeriod.year, statsPeriod.month)
      : String(statsPeriod.year);

  const options = useMemo(() => {
    const list =
      statsPeriod.mode === 'month'
        ? monthOptions(today.year, today.month)
        : yearOptions(today.year);
    // Switching mode can carry over a period outside the window; it has to stay
    // selectable, otherwise the picker would not show the current selection.
    if (list.some((option) => option.value === periodValue)) return list;
    return [...list, { value: periodValue, label: periodLabel }].sort(
      (a, b) => Number(a.value) - Number(b.value),
    );
  }, [statsPeriod.mode, today.year, today.month, periodValue, periodLabel]);

  const selectPeriod = (value: string) => {
    const parsed = Number(value);
    if (statsPeriod.mode === 'month') {
      setStatsPeriod({
        mode: 'month',
        year: Math.floor(parsed / 12),
        month: (parsed % 12) + 1,
      });
      return;
    }
    setStatsPeriod({ ...statsPeriod, year: parsed });
  };

  return (
    <div className="pb-4 nav:max-w-[720px]">
      {/* The specification puts the settings entry point in the header of every
          tab, so the period controls start below it. */}
      <TabHeader title={de.tabs.stats} showSettings={!isDesktop} />

      <div className="mx-4 rounded-card border border-line bg-card p-4 shadow-card">
        <div
          className={cn(
            'flex items-center justify-between gap-3',
            // The donut brings its own top padding, the stacked bar does not.
            statsPeriod.mode === 'month' ? 'pb-3' : 'pb-1.5',
          )}
        >
          <SegmentedControl
            options={MODE_OPTIONS}
            value={statsPeriod.mode}
            onChange={(mode) => setStatsPeriod({ ...statsPeriod, mode })}
            aria-label={de.stats.modeSwitch}
          />
          <Select value={periodValue} onValueChange={selectPeriod}>
            <SelectTrigger aria-label={de.stats.selectPeriod}>
              <SelectValue>{periodLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {statsPeriod.mode === 'month' ? (
          <>
            <StackedBar rows={summary.rows} persons={persons} />
            {summary.rows.map((row) => (
              <StatRowItem key={statRowKey(row)} row={row} persons={persons} variant="month" />
            ))}
            <p className="pt-2.5 text-hint text-fg-hint">
              {de.stats.total(formatTotalDays(summary.days))}
            </p>
          </>
        ) : (
          <>
            <Donut
              rows={summary.rows}
              persons={persons}
              year={statsPeriod.year}
              days={summary.days}
            />
            {summary.rows.map((row) => (
              <StatRowItem key={statRowKey(row)} row={row} persons={persons} variant="year" />
            ))}
            <p className="section-label pt-4 pb-1">{de.stats.averagePerMonth}</p>
            {summary.rows.map((row) => (
              <StatAverageRow key={statRowKey(row)} row={row} persons={persons} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
